import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  page: { path: '/budget-note', pageLabel: '예산 노트' },
  usageModal: {
    path: '/budget-note',
    openSelector: '.cp-budget-note-row-add, [data-tutorial-bn="row-add-usage"]',
    pageLabel: '예산 노트',
    openLabel: '집행 내역 추가',
  },
  metaColsMenu: {
    path: '/budget-note',
    openSelector: '[data-tutorial-bn="open-meta-cols"], .cp-budget-note-super-meta-btn',
    pageLabel: '예산 노트',
    openLabel: '예산 구분 및 내용',
  },
  rowMenu: {
    path: '/budget-note',
    openSelector: '[data-tutorial-bn="row-menu-trigger"]',
    pageLabel: '예산 노트',
    openLabel: '행 메뉴',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'go-budget-note': R.page,
  'view-toggle': R.page,
  'toolbar-guide': R.page,
  'add-budget-row': R.page,
  'header-cats': R.page,
  'header-memo': R.page,
  'open-meta-cols': R.page,
  'meta-cols-menu': R.metaColsMenu,
  'header-plan-budget': R.page,
  'header-project-budget': R.page,
  'fill-budget': R.page,
  'color-guide': R.page,
  'open-row-menu': R.page,
  'row-menu-guide': R.rowMenu,
  'check-balance': R.page,
  'open-usage': R.page,
  'usage-kind-guide': R.usageModal,
  'usage-modal': R.usageModal,
  'usage-block': R.page,
  'save-all': R.page,
};

const budgetNoteRaw: TutorialDefinition = {
  id: 'budget-note',
  title: '예산노트',
  summary: '사업예산을 등록하고 집행 내역을 기록·저장하는 흐름을 배워요.',
  steps: [
    {
      id: 'intro',
      mascot:
        '예산노트 튜토리얼을 시작해요! 사업예산을 추가하고 집행 내역을 기록하는 방법까지 알려드릴게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: '예산노트를 시작해요!',
      placement: 'center',
    },
    {
      id: 'go-budget-note',
      mascot: '사이드바에서 "예산 노트"를 눌러 이동해 주세요.',
      targetSelector: 'a[href^="/budget-note"]',
      placement: 'right',
      section: '[예산 노트] 시작',
      sectionMascot: '예산 노트 페이지로 이동하는 구간이에요.',
    },
    {
      id: 'view-toggle',
      mascot:
        '오른쪽 위 "가로모드"·"세로모드"를 눌러 보기가 바뀌는 걸 확인해 보세요. 이어서 진행하려면 다시 "가로모드"로 맞춘 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-todo-note-view-toggle, [data-tutorial-bn="view-toggle"]',
      manualAdvance: true,
      manualAdvanceEnableWhen:
        '[data-tutorial-bn="view-horizontal"].is-active, [data-tutorial-bn="view-horizontal"][aria-selected="true"]',
      section: '[예산 노트] 화면 둘러보기',
      sectionMascot: '보기 방식과 도구를 알아보는 구간이에요.',
    },
    {
      id: 'toolbar-guide',
      mascot:
        '찾기·필터·숨김·추경 토글로 목록을 좁힐 수 있어요. 지금은 설명만 듣고 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-budget-note-toolbar-switch-group, .cp-budget-note-search-bar, [data-tutorial-bn="toolbar-toggles"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'add-budget-row',
      mascot: '오른쪽 아래 "+"를 눌러 사업예산 행을 추가해 주세요.',
      targetSelector: '.cp-budget-note-fab, [data-tutorial-bn="fab-add-row"]',
      section: '[예산 노트] 표 구성',
      sectionMascot: '표 머리글과 입력 방법을 알아보는 구간이에요.',
    },
    {
      id: 'header-cats',
      mascot:
        '카테고리 열(1~4)로 예산 항목을 층위에 따라 기록할 수 있어요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-bn="th-cat"], .cp-budget-note-th--cat',
      manualAdvance: true,
      blockTargetClick: true,
      spotlightUnion: true,
      targetMissingHint:
        '카테고리 열이 숨겨져 있을 수 있어요. "예산 구분 및 내용"에서 열을 켠 뒤 다시 보거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'header-memo',
      mascot:
        '"메모" 열에는 예산 항목에 필요한 메모를 기록할 수 있어요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-bn="th-memo"], .cp-budget-note-th--memo',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint:
        '메모 열이 숨겨져 있을 수 있어요. "예산 구분 및 내용"에서 켠 뒤 다시 보거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'open-meta-cols',
      mascot: '"예산 구분 및 내용"을 눌러 열 보이기·숨기기 설정을 열어 주세요.',
      targetSelector: '[data-tutorial-bn="open-meta-cols"], .cp-budget-note-super-meta-btn',
      advanceWhenSelectorExists:
        '[data-tutorial-bn="meta-cols-menu"], .cp-budget-note-meta-cols-menu-pop',
    },
    {
      id: 'meta-cols-menu',
      mascot:
        '여기서 카테고리·메모 열을 숨기거나 보이게 할 수 있어요. 확인해 본 뒤 창을 닫거나 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-bn="meta-cols-menu"], .cp-budget-note-meta-cols-menu-pop',
      advanceWhenSelectorGone:
        '[data-tutorial-bn="meta-cols-menu"], .cp-budget-note-meta-cols-menu-pop',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'header-plan-budget',
      mascot:
        '"계획 예산(현재예산)"은 이 항목의 전체 계획 금액을 적어 두는 칸이에요. 참고·비교용이며, 사용액·잔액 계산에는 쓰이지 않아요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-bn="th-super-plan"], [data-tutorial-bn="th-cur"], .cp-budget-note-th--super-plan, .cp-budget-note-th--cur',
      manualAdvance: true,
      blockTargetClick: true,
      spotlightUnion: true,
    },
    {
      id: 'header-project-budget',
      mascot:
        '"사용할 예산(사업예산)"이 실제 예산 관리의 기준이에요. 집행·사용액·잔액은 모두 이 사업예산을 기준으로 계산돼요. 보통은 계획 예산과 같게 넣지만, 전체 중 일부만 할당받아 쓸 때는 다르게 넣을 수 있어요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-bn="th-super-proj"], [data-tutorial-bn="th-proj"], .cp-budget-note-th--super-proj, .cp-budget-note-th--proj',
      manualAdvance: true,
      blockTargetClick: true,
      spotlightUnion: true,
    },
    {
      id: 'fill-budget',
      mascot:
        '표에서 구분(사업명)과 예산 금액을 입력해 주세요. 입력한 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-bn="first-row"]',
      placement: 'bottom',
      manualAdvance: true,
      section: '[예산 노트] 예산 항목',
      sectionMascot: '사업예산을 입력하고 색을 넣는 구간이에요.',
      targetMissingHint:
        '입력할 행이 없어요. 앞에서 "+"로 행을 추가한 뒤 다시 진행해 주세요.',
    },
    {
      id: 'color-guide',
      mascot:
        '카테고리·메모 셀을 선택한 뒤, 위쪽 색상 칩을 누르면 항목에 표시 색을 넣을 수 있어요. "+"로 새 색도 추가할 수 있어요. 해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-bn="mark-color-palette"], .cp-budget-note-mark-color-palette, [data-tutorial-bn="table"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'open-row-menu',
      mascot: '행 왼쪽의 메뉴(☰) 버튼을 눌러 행 작업 메뉴를 열어 주세요.',
      targetSelector: '[data-tutorial-bn="row-menu-trigger"]',
      advanceWhenSelectorExists: '[data-tutorial-bn="row-menu"], .cp-budget-note-row-menu-pop',
      targetMissingHint:
        '행이 있어야 메뉴 버튼이 보여요. 앞에서 행을 추가한 뒤 다시 진행해 주세요.',
    },
    {
      id: 'row-menu-guide',
      mascot:
        '눈 아이콘은 행 숨기기, 복제는 길게 눌러 바로 아래 행을 만들고, 휴지통은 길게 눌러 행을 삭제해요. 지금은 살펴만 보고 메뉴를 닫거나 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-bn="row-menu"], .cp-budget-note-row-menu-pop',
      advanceWhenSelectorGone: '[data-tutorial-bn="row-menu"], .cp-budget-note-row-menu-pop',
      manualAdvance: true,
      blockTargetClick: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'check-balance',
      mascot:
        '사용액·잔액은 집행을 넣을수록 자동으로 계산돼요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-budget-note-td--used, .cp-budget-note-td--bal, .cp-budget-note-vert-field--used, .cp-budget-note-vert-field--bal, [data-tutorial-bn="bal-used"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'open-usage',
      mascot: '행의 "+"를 눌러 집행(지출) 내역을 추가해 주세요.',
      targetSelector: '.cp-budget-note-row-add, [data-tutorial-bn="row-add-usage"]',
      advanceWhenSelectorExists: '.cp-budget-note-modal',
      section: '[예산 노트] 집행 내역',
      sectionMascot: '실제 집행을 기록하는 구간이에요.',
      targetMissingHint:
        '예산 행이 있어야 "+"가 보여요. 앞에서 행을 추가한 뒤 다시 진행해 주세요.',
    },
    {
      id: 'usage-kind-guide',
      mascot:
        '"집행예정"은 사용액·잔액에 반영되지 않아요. "집행완료"로 두면 그때 사용액과 잔액에 반영돼요. 눌러 바꿔 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-bn="usage-kind"], .cp-budget-note-usage-kind-tabs, .cp-budget-note-usage-tab-planned, .cp-budget-note-usage-tab-completed',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'usage-modal',
      mascot:
        '날짜·금액을 채운 뒤 "추가"를 눌러 주세요. 취소로는 넘어갈 수 없어요.',
      targetSelector: '.cp-budget-note-modal, [data-tutorial-bn="usage-modal"]',
      advanceSelector:
        '.cp-budget-note-modal .cp-btn-primary:not([disabled]), [data-tutorial-bn="usage-submit"]:not([disabled])',
      blockClickSelectors: '.cp-budget-note-modal .cp-btn-secondary, .cp-budget-note-modal [aria-label="닫기"]',
    },
    {
      id: 'usage-block',
      mascot:
        '표에 생긴 집행 블록을 누르면 수정할 수 있어요. 지금은 살펴만 보고 "다음"을 눌러 주세요.',
      targetSelector: '.cp-budget-note-block, [data-tutorial-bn="usage-block"]',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint:
        '집행 블록이 아직 없어요. 앞에서 집행을 추가한 뒤 다시 보거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'save-all',
      mascot: '변경한 내용을 "저장하기"로 마무리해 주세요.',
      targetSelector: '.cp-btn-save-all:not([disabled]), .cp-fab-save:not([disabled])',
      manualAdvance: true,
      section: '[예산 노트] 저장',
      sectionMascot: '작업한 내용을 저장하는 구간이에요.',
      targetMissingHint:
        '저장할 변경이 없으면 버튼이 비활성일 수 있어요. 이미 반영했다면 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'outro',
      mascot:
        '예산노트 튜토리얼을 마쳤어요! 필요할 때 계정 메뉴의 "튜토리얼"에서 다시 볼 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const budgetNote: TutorialDefinition = {
  ...budgetNoteRaw,
  steps: budgetNoteRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint: buildTutorialTargetMissingHint(recover, step.targetMissingHint),
    };
  }),
};

export default budgetNote;
